import get from 'lodash/get';
import { snakeCase as lodashSnakeCase } from 'lodash';
import camelCase from 'lodash/camelCase';
import { HQ_USER_TYPE } from '../../../constants/hq-types';
import { isSingleCompanyAssignee } from '../../../utils/assigned-to-helpers';
import { ISSUE_ATTRIBUTE_NAMES } from '../../../constants/issue-attribute-names';
import { getStatusFromServerStatus } from '../../../utils/transition-helpers';
import {
  STATUS_ANSWERED,
  STATUS_CLOSED,
  STATUS_DRAFT,
  STATUS_OPEN,
  STATUS_OPEN_REV1,
  STATUS_OPEN_REV2,
  STATUS_REJECTED,
  STATUS_ANSWERED_MANAGER,
  STATUS_REJECTED_MANAGER,
} from '../../../constants/status-types';
import { FE_TRANSITION_FIELDS } from '../../../constants/transitions';
import { channelItemTypes } from '../../../constants/channel-items';
import { toNativeIsoDate } from '../../../utils/date-helpers';

const toAttributeFormat = ({ fieldName, isMandatory = false, values, sortOrder = -1 }) => {
  return {
    fieldName,
    isMandatory,
    values,
    sortOrder,
  };
};

const normalizeAttributesMap = (transitionType, isOpenRev2) => {
  switch (transitionType) {
    case 'void':
      return [ISSUE_ATTRIBUTE_NAMES.COMMENTS_ATTRIBUTES].map(fieldName => toAttributeFormat({ fieldName }));
    case 'closed-answered':
    case 'closed-rejected':
    case 'closed-submitted':
      return [
        ...[
          ISSUE_ATTRIBUTE_NAMES.COMMENTS_ATTRIBUTES,
        ].map((fieldName) => toAttributeFormat({ fieldName })),
        {
          fieldName: FE_TRANSITION_FIELDS.CREATE_REVISION,
        }
      ];
    case 'submitted-open':
    case 'draft-open':
      return [
        toAttributeFormat({
          fieldName: ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER,
          isMandatory: true,
          sortOrder: 1,
        }),
        ...[
          ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS,
          ISSUE_ATTRIBUTE_NAMES.DUE_DATE,
          ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST,
        ].map((fieldName) => toAttributeFormat({ fieldName })),
      ];

    case 'submitted-closed':
      return [
        toAttributeFormat({
          fieldName: ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
          isMandatory: true,
          sortOrder: 1,
        }),
        toAttributeFormat({ fieldName: ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST }),
      ];
    case 'open-answered':
    case 'open-rejected':
    case 'answered-answered':
    case 'answered-rejected':
    case 'rejected-answered':
    case 'rejected-rejected':
      return [
        toAttributeFormat({
          fieldName: ISSUE_ATTRIBUTE_NAMES.TRANSITION_ID,
          isMandatory: true,
          sortOrder: 1,
        }),
      ];

    case 'answered-open':
    case 'rejected-open':
      if (isOpenRev2) {
        return [
          toAttributeFormat({
            fieldName: ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
            isMandatory: true,
            sortOrder: 1,
          }),
          ...[
            ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS,
            ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST,
            ISSUE_ATTRIBUTE_NAMES.COMMENTS_ATTRIBUTES,
          ].map((fieldName) => toAttributeFormat({ fieldName })),
        ];
      }
      return [
        toAttributeFormat({
          fieldName: ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
          isMandatory: true,
          sortOrder: 1,
        }),
        ...[
          ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS,
          ISSUE_ATTRIBUTE_NAMES.DUE_DATE,
          ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST,
          ISSUE_ATTRIBUTE_NAMES.COMMENTS_ATTRIBUTES,
        ].map((fieldName) => toAttributeFormat({ fieldName })),
      ];

    case 'answered-closed':
    case 'rejected-closed':
      return [
        ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
        ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST,
        ISSUE_ATTRIBUTE_NAMES.COMMENTS_ATTRIBUTES,
      ].map((fieldName) => toAttributeFormat({ fieldName }));
    case 'open-open': // workflow B - openRev1 - openRev2
      return [toAttributeFormat({ fieldName: ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS })];
    default:
      return [];
  }
};

const mergeAttributes = (attributesFromServer, localAttributes) => {
  return [
    ...attributesFromServer,
    ...localAttributes.filter(({ isMandatory }) => {
      return isMandatory;
    }),
  ]
    .sort(({ sortOrder: a = 0 }, { sortOrder: b = 0 }) => {
      return b - a;
    })
    .map((attr) => attr.name || attr.fieldName);
};

const getTransitionAssigneeValue = ({ issue, transition, status }) => {
  // in some cases the assignee of the transition is sent from the server in the issue props
  // this function sets the assignee accordingly
  const { status: transitionStatus } = transition;
  if (transitionStatus === STATUS_CLOSED) {
    return issue.createdBy;
  }
  if (
    (status === STATUS_ANSWERED || status === STATUS_REJECTED) &&
    transitionStatus === STATUS_OPEN
  ) {
    return issue.architectId;
  }
  if (
    status === STATUS_OPEN &&
    (transitionStatus === STATUS_ANSWERED || transitionStatus === STATUS_REJECTED)
  ) {
    return issue.managerId;
  }
  if (
    (status === STATUS_REJECTED || status === STATUS_ANSWERED) &&
    transitionStatus === STATUS_OPEN_REV1
  ) {
    return issue.constructionManagerId;
  }
  if (
    (status === STATUS_REJECTED || status === STATUS_ANSWERED) &&
    transitionStatus === STATUS_OPEN_REV2
  ) {
    return issue.architectId;
  }
  if (
    (status === STATUS_REJECTED || status === STATUS_ANSWERED) &&
    (transitionStatus === STATUS_ANSWERED_MANAGER || transitionStatus === STATUS_REJECTED_MANAGER)
  ) {
    return issue.managerId;
  }
  return null;
};
export default class {
  constructor({ oxygen }) {
    this.oxygen = oxygen;

    this.deserializeRfi = this.deserializeRfi.bind(this);
    this.deserializeAttachment = this.deserializeAttachment.bind(this);
    this.deserializeChangeset = this.deserializeChangeset.bind(this);
    this.deserializeComment = this.deserializeComment.bind(this);
  }

  deserializeTransition({ status, requiredAttributes, values, currentStatus, permittedAssignee }) {
    const normalizeStatus = getStatusFromServerStatus(status);
    const assignedToAttr = requiredAttributes.find((attr) => attr.name === 'assignedTo');
    const assignees = permittedAssignee
      ? [{ id: permittedAssignee, type: HQ_USER_TYPE }]
      : (values
          ? values.assignedTo
          : get(assignedToAttr, 'values', [])
        ).map(({ value: id, type }) => ({ id, type }));
    const optionalAttributes = normalizeAttributesMap(
      normalizeStatus === 'void' ? 'void' : `${currentStatus}-${normalizeStatus}`,
      status === STATUS_OPEN_REV2
    );
    return {
      status: normalizeStatus,
      serverStatus: status,
      required_attributes: mergeAttributes(requiredAttributes, optionalAttributes),
      optionalAttributes: optionalAttributes.filter(({ isMandatory }) => {
        return !isMandatory;
      }),
      assignees,
      values: { ...values, assignedTo: assignees },
      v2: true,
    };
  }

  deserializeRfi({
    dueDate = null,
    permittedActions,
    status: serverStatus,
    linkedDocument: targetUrn = null,
    linkedDocumentVersion: startingVersion = null,
    attachmentsCount,
    commentsCount,
    ...restRfiAttributes
  }) {
    const { oxygen } = this;
    const {
      assignedTo = null,
      location = {},
      distributionList,
      respondedBy,
      createdBy,
    } = restRfiAttributes;

    const normalizeStatus = getStatusFromServerStatus(serverStatus);
    const assignedToDisplayName = oxygen.getDisplayName(assignedTo);
    const currentAssignee = oxygen.getAssigneesDisplayName([{ id: assignedTo }]);
    const assignees = [{ id: assignedTo, type: HQ_USER_TYPE }];
    const companyName = isSingleCompanyAssignee(assignees)
      ? assignedToDisplayName
      : oxygen.getCompanyDisplayNameByUserId(assignedTo);
    const locationDescription = location.description || '';
    const createdByDisplayNameWithCompanyName = oxygen.getAssigneesDisplayName([{ id: createdBy }]);
    const answeredByDisplayName = oxygen.getDisplayName(respondedBy);
    const distributionListDisplayNames = distributionList
      ? distributionList.map(oxygen.getDisplayName, oxygen)
      : '';
    const permittedAssignTo =
      get(permittedActions, 'updateRfi.permittedAttributes', []).find(
        (attr) => attr.name === ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO
      ) || {};
    const currentAllowedAssignees = permittedAssignTo.values
      ? permittedAssignTo.values.map(({ value: id, type }) => ({ id, type }))
      : [];
    const permittedActionsDeserilized = { ...permittedActions };

    if (permittedActionsDeserilized.updateRfi) {
      if (permittedActionsDeserilized.updateRfi.permittedAttributes) {
        if (
          permittedActionsDeserilized.updateRfi.permittedAttributes.find(
            ({ name }) => name === ISSUE_ATTRIBUTE_NAMES.LOCATION
          )
        ) {
          permittedActionsDeserilized.updateRfi.permittedAttributes.push({
            name: ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION,
          });
        }
        if (
          permittedActionsDeserilized.updateRfi.permittedAttributes.find(
            ({ name }) => name === ISSUE_ATTRIBUTE_NAMES.QUESTION
          )
        ) {
          permittedActionsDeserilized.updateRfi.permittedAttributes.push({
            name: ISSUE_ATTRIBUTE_NAMES.DESCRIPTION,
          });
        }
        if (
          permittedActionsDeserilized.updateRfi.permittedAttributes.find(
            ({ name }) => name === ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENT_CLOSE_VERSION
          )
        ) {
          permittedActionsDeserilized.updateRfi.permittedAttributes.push({
            name: ISSUE_ATTRIBUTE_NAMES.CLOSE_VERSION,
          });
        }
        permittedActionsDeserilized.updateRfi.permittedAttributes = permittedActionsDeserilized.updateRfi.permittedAttributes.map(
          ({ name }) => {
            return { name: lodashSnakeCase(name) };
          }
        );
      }
      const isAnswerStatus = ({ status }) => {
        return status === 'answered' || status === 'rejected';
      };
      const statuses = permittedActions.updateRfi.permittedStatuses || [];
      permittedActionsDeserilized.updateRfi.permittedStatuses = statuses.map((transitionStatus) => {
        const permittedAssignee = getTransitionAssigneeValue({
          issue: restRfiAttributes,
          transition: transitionStatus,
          status: normalizeStatus,
        });
        return this.deserializeTransition({
          ...transitionStatus,
          currentStatus: normalizeStatus,
          permittedAssignee,
        });
      });
      const answerOptions = permittedActionsDeserilized.updateRfi.permittedStatuses.filter(
        isAnswerStatus
      );
      permittedActionsDeserilized.updateRfi.permittedStatuses = permittedActionsDeserilized.updateRfi.permittedStatuses.map(
        (status) => {
          return isAnswerStatus(status)
            ? { ...status, values: { ...status.values, transitionId: answerOptions } }
            : status;
        }
      );
      if (isAnswerStatus({ status: normalizeStatus })) {
        permittedActionsDeserilized.updateRfi.permittedStatuses = permittedActionsDeserilized.updateRfi.permittedStatuses.sort(
          ({ status: a }, { status: b }) => {
            if (a === STATUS_CLOSED) {
              return -1;
            }
            if (b === STATUS_CLOSED) {
              return 1;
            }
            return 0;
          }
        );
      }
    }

    return {
      ...restRfiAttributes,
      // In case commentsCount or attachmentsCount does not exist we want to keep the
      // existing values in the state, this is a workaround for SCPJM-5317
      ...(typeof commentsCount === 'number' && { commentCount: commentsCount }),
      ...(typeof attachmentsCount === 'number' && { attachmentCount: attachmentsCount }),
      permittedActions,
      status: normalizeStatus,
      serverStatus,
      assignedToDisplayName,
      currentAssignee,
      assignees,
      answer: restRfiAttributes.answer || restRfiAttributes.officialResponse,
      description: restRfiAttributes.description || restRfiAttributes.question,
      companyName,
      locationDescription,
      targetUrn,
      createdByDisplayNameWithCompanyName,
      answeredByDisplayName,
      currentAllowedAssignees,
      startingVersion,
      distributionListDisplayNames,
      dueDate,
      type: 'rfis',
    };
  }

  deserializeRfis({ results, pagination }) {
    const getNormalizePagination = ({ limit, offset, totalResults }) => {
      return {
        meta: {
          page: {
            offset,
            limit,
          },
          recordCount: totalResults,
        },
      };
    };

    const rfisResults = results.map((rfi) => {
      const newRfi = this.deserializeRfi(rfi);
      delete newRfi.permittedActions;
      return newRfi;
    });
    // insertPagination in the first argument for v1 compatibility
    if (rfisResults.length) {
      const normalizePagination = getNormalizePagination(pagination);
      rfisResults[0] = { ...rfisResults[0], ...normalizePagination };
    }

    return rfisResults;
  }

  deserializeUser({ user, workflow, permittedActions, containerVirtualFolderUrn, maintenanceEndDate }) {
    if (maintenanceEndDate) {
      return { maintenanceEndDate };
    }
    const getInitialData = (userId, permittedStatuses = []) => {
      const canCreateDraftRfi = permittedStatuses.length > 0;
      if (!canCreateDraftRfi) {
        return undefined;
      }

      const permittedStatusesExcludeDraft = permittedStatuses.filter(
        ({ status }) => status !== 'draft'
      );

      const transitions = permittedStatusesExcludeDraft.map(this.deserializeTransition);

      return [
        {
          status: STATUS_DRAFT,
          serverStatus: STATUS_DRAFT,
          transitions,
          assignees: [
            {
              id: userId,
              type: 'user',
            },
          ],
        },
      ];
    };
    const getWorkflowRoles = (roles) => {
      const convertV1RolesToV2 = (roleName) => roleName.toLowerCase().replace('project', '');
      return roles.map(convertV1RolesToV2);
    };

    const isProjectAdmin = user.role === 'project_admin';
    const workflowRoles = getWorkflowRoles(workflow.roles);
    const workflowType = workflow.type;
    const permittedStatuses = get(permittedActions, 'createRfi.permittedStatuses');
    const initialData = getInitialData(user.id, permittedStatuses);
    const newData = initialData ? { initialData } : undefined;
    const displayName = this.oxygen.getDisplayName(user.id);
    return {
      displayName,
      isProjectAdmin,
      containerVirtualFolderUrn,
      maintenanceEndDate,
      rfis: {
        workflowRoles,
        workflowType,
        new: newData,
      },
      ...user,
    };
  }

  deserializeAttachment({ rfiId, createdBy, deletedBy, ...restAttachmentAttr }) {
    const createdByDisplayName = this.oxygen.getDisplayName(createdBy);
    const deletedByDisplayName = this.oxygen.getDisplayName(deletedBy);
    const createdByDisplayNameWithCompanyName = this.oxygen.getAssigneesDisplayName([
      { id: createdBy },
    ]);
    return {
      ...restAttachmentAttr,
      createdBy,
      createdByDisplayName,
      createdByDisplayNameWithCompanyName,
      deletedBy,
      deletedByDisplayName,
      issueId: rfiId,
      type: channelItemTypes.ATTACHMENTS,
    };
  }

  deserializeAttachments({ results = [], _pagination }) {
    return results.map(this.deserializeAttachment);
  }

  deserializeComment({ createdBy, ...restAttachmentAttr }) {
    const { oxygen } = this;
    const createdByDisplayName = oxygen.getDisplayName(createdBy);
    return {
      ...restAttachmentAttr,
      type: channelItemTypes.COMMENTS,
      createdByDisplayName,
    };
  }

  deserializeComments({ results = [], _pagination }) {
    return results.map(this.deserializeComment);
  }

  deserializeChangeset({ changes: originalChanges, ...restAttachmentAttr }) {
    const { oxygen } = this;
    const { createdBy, deletedBy } = restAttachmentAttr;

    const changes = originalChanges.reduce(
      (accumulator, { propertyName, oldValue = null, newValue }) => {
        let fieldName = camelCase(propertyName);
        if (fieldName === ISSUE_ATTRIBUTE_NAMES.QUESTION) {
          fieldName = ISSUE_ATTRIBUTE_NAMES.DESCRIPTION;
        }
        if (fieldName === ISSUE_ATTRIBUTE_NAMES.LOCATION) {
          fieldName = ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION;
        }
        accumulator[fieldName] = [oldValue, newValue];
        return accumulator;
      },
      {}
    );

    const {
      assignedTo: assignedToOriginal,
      coReviewers: coReviewersOriginal,
      distributionList: distributionListOriginal,
    } = changes;
    const assignedTo = assignedToOriginal
      ? assignedToOriginal.map(oxygen.getDisplayName, this.oxygen)
      : null;
    const coReviewers = coReviewersOriginal
      ? coReviewersOriginal.map((arr) => (arr || []).map(oxygen.getDisplayName, this.oxygen))
      : null;
    const distributionList = distributionListOriginal
      ? distributionListOriginal.map((arr) => (arr || []).map(oxygen.getDisplayName, this.oxygen))
      : null;

    const createdByDisplayName = oxygen.getDisplayName(createdBy);
    const deletedByDisplayName = oxygen.getDisplayName(deletedBy);

    const changesWithOxygen = {
      ...changes,
      assignedTo,
      coReviewers,
      distributionList,
    };

    return {
      ...restAttachmentAttr,
      changesWithOxygen,
      type: channelItemTypes.CHANGESETS,
      changes,
      createdByDisplayName,
      deletedByDisplayName,
    };
  }

  deserializeChangesets({ results = [], _pagination }) {
    return results.map(this.deserializeChangeset);
  }

  deserializeWorkflows(workflows) {
    const buildStates =
      (workflows &&
        workflows.projectRolesMapping.map((projectRole) => ({
          id: projectRole.name,
          name: projectRole.name.replace('project', '').toLowerCase(),
          description: '',
          data: { assignees: projectRole.permittedAssignees },
        }))) ||
      [];
    return [
      {
        wfType: workflows.workflowType,
        states: buildStates,
      },
    ];
  }

  serializeRfi({
    dueDate: originalDueDate,
    assignedTo: originalAssignedTo,
    answer,
    officialResponse = answer,
    locationDescription,
    location: locationValue,
    description,
    attachmentsAttributes,
    pushpinAttributes,
    targetUrn: linkedDocument,
    startingVersion: linkedDocumentVersion,
    reserveCustomIdentifier,
    customIdentifier: originalCustomIdentifier,
    ...restAttributes
  }) {
    const isLocationEmpty = !locationDescription && !locationValue;

    const computedAttributes = {
      location: isLocationEmpty ? undefined : { description: locationValue || locationDescription },
    };
    const question = description;
    const serializedAttachmentsAttributes = attachmentsAttributes
      ? attachmentsAttributes.map(({ urn_type, ...rest }) => ({ urnType: urn_type, ...rest }))
      : undefined;

    const deleteDueDate = originalDueDate === null;
    const dueDate = deleteDueDate ? null : toNativeIsoDate(originalDueDate);
    const deleteCustomIdentifier = originalCustomIdentifier === '';
    const customIdentifier = deleteCustomIdentifier
      ? null
      : originalCustomIdentifier || reserveCustomIdentifier;

    return {
      ...restAttributes,
      ...computedAttributes,
      question,
      attachmentsAttributes: serializedAttachmentsAttributes,
      assignedTo: originalAssignedTo || undefined,
      officialResponse,
      customIdentifier,
      dueDate,
      pushpinAttributes,
      linkedDocument,
      linkedDocumentVersion,
    };
  }

  serializeProjectRolesMapping(id, { data: { assignees } }) {
    return {
      projectRolesMapping: [
        {
          name: id,
          permittedAssignees: assignees,
        },
      ],
    };
  }
}
